import React from 'react';
import { ContextualMenuItemType, DefaultButton, IconButton, IContextualMenuItem } from '@fluentui/react';

interface ISearchPagerProps {
    page: number;
    pageSize: number;
    total: number;
    onPageChange: (page: number) => void;
    /** Shows a "Results per page" section when provided. */
    onPageSizeChange?: (size: number) => void;
}

/** Deep-paging cap carried over from the old bottom Paginator (it listed at most 10 pages). */
const MAX_PAGES = 10;
const PAGE_SIZES = [25, 50, 100];

/**
 * Compact dropdown pager, the admin-web (Fluent v8) equivalent of the storefront runtime's
 * Paginator: prev/next chevrons around a "start-end of total" trigger whose menu jumps to any
 * page and picks the page size. Small enough to live in a command bar — it replaces both the
 * old read-only count text and the fixed paginator bar that used to sit over the results.
 */
const SearchPager: React.FC<ISearchPagerProps> = (props) => {
    const { page, pageSize, total } = props;
    if (!total) return null;
    const totalPages = Math.min(Math.ceil(total / pageSize), MAX_PAGES);
    const current = Math.min(Math.max(page, 1), totalPages);
    const start = (current - 1) * pageSize + 1;
    const end = Math.min(current * pageSize, total);

    const goTo = (target: number) => {
        if (target < 1 || target > totalPages || target === current) return;
        props.onPageChange(target);
    };

    const menuItems: IContextualMenuItem[] = [
        { key: 'pageHeader', itemType: ContextualMenuItemType.Header, text: `Page ${current} of ${totalPages}` },
        ...Array.from({ length: totalPages }, (_, i) => i + 1).map((i): IContextualMenuItem => ({
            key: `page-${i}`,
            text: `Page ${i}`,
            disabled: i === current,
            onClick: () => goTo(i),
        })),
        ...(props.onPageSizeChange ? [
            { key: 'sizeDivider', itemType: ContextualMenuItemType.Divider } as IContextualMenuItem,
            { key: 'sizeHeader', itemType: ContextualMenuItemType.Header, text: 'Results per page' } as IContextualMenuItem,
            ...PAGE_SIZES.map((s): IContextualMenuItem => ({
                key: `size-${s}`,
                text: String(s),
                disabled: s === pageSize,
                onClick: () => props.onPageSizeChange?.(s),
            })),
        ] : []),
    ];

    return (
        <div style={{ display: 'inline-flex', alignItems: 'center', height: '100%' }}>
            <IconButton
                iconProps={{ iconName: 'ChevronLeft' }}
                title='Previous page'
                ariaLabel='Previous page'
                disabled={current <= 1}
                onClick={() => goTo(current - 1)}
            />
            <DefaultButton
                text={`${start}-${end} of ${total}`}
                title='Go to page'
                ariaLabel={`Results ${start} through ${end} of ${total}. Go to page`}
                menuProps={{ items: menuItems }}
                styles={{ root: { border: 'none', padding: '0 4px', minWidth: 0, backgroundColor: 'transparent' } }}
            />
            <IconButton
                iconProps={{ iconName: 'ChevronRight' }}
                title='Next page'
                ariaLabel='Next page'
                disabled={current >= totalPages}
                onClick={() => goTo(current + 1)}
            />
        </div>
    );
};

export default SearchPager;
